import { useEffect, useMemo, useRef, useState } from 'react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErroCarregamento, { extrairMensagemErro } from '../../components/common/ErroCarregamento';
import StatusBadge from '../../components/common/StatusBadge';
import Pill from '../../components/common/Pill';
import CoreBadge from '../../components/common/CoreBadge';
import AvaliacaoForm from './AvaliacaoForm';
import HistoricoModal from './HistoricoModal';
import { formatDateOnly } from '../../utils/date';
import { STATUS_AVALIACAO_OPCOES, STATUS_PRAZO_LABELS, HOSPITALAR_LABELS } from '../../utils/acreditacaoOpcoes';
import { requisitosDaSubsecao } from '../../utils/catalogo';
import * as catalogoApi from '../../api/catalogo';
import * as hospitaisApi from '../../api/hospitais';
import * as avaliacoesApi from '../../api/avaliacoes';

const ATENDIDO = new Set(['conforme', 'supera']);

const FILTROS_STATUS = [
  { value: 'todos', label: 'Todos' },
  { value: 'nao_avaliado', label: 'Não avaliado' },
  ...STATUS_AVALIACAO_OPCOES.map((o) => ({ value: o.value, label: o.label })),
];

const FILTROS_VIGENCIA = [
  { value: 'todos', label: 'Todos' },
  { value: 'em_dia', label: STATUS_PRAZO_LABELS.em_dia },
  { value: 'a_vencer', label: STATUS_PRAZO_LABELS.a_vencer },
  { value: 'vencido', label: STATUS_PRAZO_LABELS.vencido },
];

const FILTROS_OBRIGATORIEDADE = [
  { value: 'todos', label: 'Todos' },
  ...Object.entries(HOSPITALAR_LABELS).map(([value, label]) => ({ value, label })),
];

export default function AbaMapa({ idHospital, somenteLeitura, requisitoAlvo, onRequisitoAlvoVisto }) {
  const [secoes, setSecoes] = useState(null);
  const [escopo, setEscopo] = useState(null);
  const [avaliacoes, setAvaliacoes] = useState(null);
  const [secoesAbertas, setSecoesAbertas] = useState(new Set());
  const [subsecoesAbertas, setSubsecoesAbertas] = useState(new Set());
  const [modalAvaliar, setModalAvaliar] = useState(null); // requisito
  const [modalHistorico, setModalHistorico] = useState(null); // requisito
  const [filtroCore, setFiltroCore] = useState('todos'); // 'todos' | 'core'
  const [filtroObrigatoriedade, setFiltroObrigatoriedade] = useState('todos');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroVigencia, setFiltroVigencia] = useState('todos');
  const [busca, setBusca] = useState('');
  const [destacado, setDestacado] = useState(null);
  const [erro, setErro] = useState('');
  const requisicaoAtual = useRef(0);
  const buscaAtiva = busca.trim().length >= 2;

  const carregar = async () => {
    const minhaRequisicao = ++requisicaoAtual.current;
    setErro('');
    try {
      const [resCatalogo, resEscopo, resAvaliacoes] = await Promise.all([
        catalogoApi.listarHierarquia(),
        hospitaisApi.obterEscopo(idHospital),
        avaliacoesApi.listarAtuais(idHospital),
      ]);
      if (requisicaoAtual.current !== minhaRequisicao) return; // resposta obsoleta (hospital já trocou)
      setSecoes(resCatalogo.data);
      setEscopo(resEscopo.data);
      setAvaliacoes(resAvaliacoes.data);
    } catch (err) {
      if (requisicaoAtual.current !== minhaRequisicao) return;
      setErro(extrairMensagemErro(err, 'Não foi possível carregar o mapa de conformidade.'));
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idHospital]);

  useEffect(() => {
    if (!requisitoAlvo || !secoes) return;

    // limpa os filtros — senão o requisito pode ficar escondido pelo filtro ativo
    setFiltroCore('todos');
    setFiltroObrigatoriedade('todos');
    setFiltroStatus('todos');
    setFiltroVigencia('todos');
    setBusca('');

    for (const secao of secoes) {
      for (const sub of secao.subsecoes) {
        if (requisitosDaSubsecao(sub).some((r) => r.id_requisito === requisitoAlvo)) {
          setSecoesAbertas((s) => new Set(s).add(secao.id_secao));
          setSubsecoesAbertas((s) => new Set(s).add(sub.id_subsecao));
        }
      }
    }

    setDestacado(requisitoAlvo);
    onRequisitoAlvoVisto?.();

    const timer = setTimeout(() => {
      document.getElementById(`requisito-${requisitoAlvo}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requisitoAlvo, secoes]);

  const ativosPorId = useMemo(() => {
    const mapa = new Map();
    (escopo || []).forEach((e) => mapa.set(e.id_requisito, e.ativo));
    return mapa;
  }, [escopo]);

  const avaliacaoPorRequisito = useMemo(() => {
    const mapa = new Map();
    (avaliacoes || []).forEach((a) => mapa.set(a.id_requisito, a));
    return mapa;
  }, [avaliacoes]);

  const statsAtendidos = useMemo(() => {
    if (!secoes) return { total: 0, atendidos: 0 };
    let total = 0;
    let atendidos = 0;
    secoes.forEach((secao) => {
      secao.subsecoes.forEach((sub) => {
        if (filtroObrigatoriedade !== 'todos' && sub.hospitalar !== filtroObrigatoriedade) return;
        requisitosDaSubsecao(sub).forEach((r) => {
          if (!ativosPorId.get(r.id_requisito)) return;
          if (filtroCore === 'core' && !r.core) return;
          total += 1;
          const avaliacao = avaliacaoPorRequisito.get(r.id_requisito);
          if (avaliacao && ATENDIDO.has(avaliacao.status)) atendidos += 1;
        });
      });
    });
    return { total, atendidos };
  }, [secoes, ativosPorId, avaliacaoPorRequisito, filtroCore, filtroObrigatoriedade]);

  if (!secoes || !escopo || !avaliacoes) {
    return erro ? <ErroCarregamento mensagem={erro} onRetry={carregar} /> : <LoadingSpinner />;
  }

  const toggleSecao = (id) =>
    setSecoesAbertas((s) => {
      const novo = new Set(s);
      novo.has(id) ? novo.delete(id) : novo.add(id);
      return novo;
    });

  const toggleSubsecao = (id) =>
    setSubsecoesAbertas((s) => {
      const novo = new Set(s);
      novo.has(id) ? novo.delete(id) : novo.add(id);
      return novo;
    });

  const passaFiltros = (r) => {
    if (!ativosPorId.get(r.id_requisito)) return false;
    if (filtroCore === 'core' && !r.core) return false;
    const avaliacao = avaliacaoPorRequisito.get(r.id_requisito);
    if (filtroStatus !== 'todos') {
      const statusAtual = avaliacao ? avaliacao.status : 'nao_avaliado';
      if (statusAtual !== filtroStatus) return false;
    }
    if (filtroVigencia !== 'todos') {
      if (!avaliacao || avaliacao.status_vigencia !== filtroVigencia) return false;
    }
    if (buscaAtiva) {
      const alvo = `${r.codigo} ${r.texto}`.toLowerCase();
      if (!alvo.includes(busca.trim().toLowerCase())) return false;
    }
    return true;
  };

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-3">
        <CoreBadge size={22} />
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--am-tinta)' }}>
            {statsAtendidos.atendidos} de {statsAtendidos.total} requisitos
            {filtroCore === 'core' ? ' CORE' : ''}
            {filtroObrigatoriedade !== 'todos' ? ` (${HOSPITALAR_LABELS[filtroObrigatoriedade]})` : ''} atendidos
          </div>
          <div style={{ fontSize: 12, color: 'var(--am-cinza-texto-2)' }}>
            {filtroCore === 'core'
              ? 'Requisitos de alta prioridade/segurança no escopo ativo'
              : 'Todos os requisitos no escopo ativo'}
          </div>
        </div>
      </div>

      <label className="am-field" style={{ maxWidth: 420, marginBottom: 14 }}>
        <div className="am-field__label-row">
          <span className="am-field__label">Buscar requisito</span>
        </div>
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Código ou texto do requisito..."
        />
      </label>

      <div className="filtros-painel mb-3">
        <div className="filtros-painel__linha">
          <span className="filtros-painel__label">Escopo</span>
          <div className="filter-tabs">
            <button
              type="button"
              className={`filter-tab${filtroCore === 'todos' ? ' is-active' : ''}`}
              onClick={() => setFiltroCore('todos')}
            >
              Todos
            </button>
            <button
              type="button"
              className={`filter-tab${filtroCore === 'core' ? ' is-active' : ''}`}
              onClick={() => setFiltroCore('core')}
            >
              Somente CORE
            </button>
          </div>
          <span className="filtros-painel__divisor" />
          <div className="filter-tabs">
            {FILTROS_OBRIGATORIEDADE.map((f) => (
              <button
                key={f.value}
                type="button"
                className={`filter-tab${filtroObrigatoriedade === f.value ? ' is-active' : ''}`}
                onClick={() => setFiltroObrigatoriedade(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="filtros-painel__linha">
          <span className="filtros-painel__label">Status</span>
          <div className="filter-tabs">
            {FILTROS_STATUS.map((f) => (
              <button
                key={f.value}
                type="button"
                className={`filter-tab${filtroStatus === f.value ? ' is-active' : ''}`}
                onClick={() => setFiltroStatus(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="filtros-painel__linha">
          <span className="filtros-painel__label">Vigência</span>
          <div className="filter-tabs">
            {FILTROS_VIGENCIA.map((f) => (
              <button
                key={f.value}
                type="button"
                className={`filter-tab${filtroVigencia === f.value ? ' is-active' : ''}`}
                onClick={() => setFiltroVigencia(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="d-flex flex-column gap-2">
        {secoes.map((secao) => {
          const subsecoesComAtivos = secao.subsecoes
            .filter((sub) => filtroObrigatoriedade === 'todos' || sub.hospitalar === filtroObrigatoriedade)
            .map((sub) => ({
              sub,
              requisitosFiltrados: requisitosDaSubsecao(sub).filter(passaFiltros),
            }))
            .filter((s) => s.requisitosFiltrados.length > 0);

          if (subsecoesComAtivos.length === 0) return null;

          const totalFiltrados = subsecoesComAtivos.reduce((acc, s) => acc + s.requisitosFiltrados.length, 0);
          const aberta = secoesAbertas.has(secao.id_secao) || buscaAtiva;

          return (
            <div key={secao.id_secao} className="card-soft" style={{ overflow: 'hidden' }}>
              <button
                type="button"
                className="d-flex align-items-center gap-3 w-100 border-0 bg-transparent text-start"
                style={{ padding: '16px 20px', cursor: 'pointer' }}
                onClick={() => toggleSecao(secao.id_secao)}
              >
                <span className="ms" style={{ fontSize: 20, color: 'var(--am-cinza-texto-2)' }}>
                  {aberta ? 'expand_more' : 'chevron_right'}
                </span>
                <div className="flex-grow-1">
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--am-tinta)' }}>
                    Seção {secao.numero} — {secao.nome}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--am-cinza-texto-2)' }}>
                    {totalFiltrados} requisito(s) nesse filtro
                  </div>
                </div>
              </button>

              {aberta && (
                <div style={{ borderTop: '1px solid var(--am-borda-2)' }}>
                  {subsecoesComAtivos.map(({ sub, requisitosFiltrados }) => {
                    const subAberta = subsecoesAbertas.has(sub.id_subsecao) || buscaAtiva;
                    return (
                      <div key={sub.id_subsecao} style={{ borderBottom: '1px solid var(--am-borda-2)' }}>
                        <button
                          type="button"
                          className="d-flex align-items-center gap-3 w-100 border-0 bg-transparent text-start"
                          style={{ padding: '13px 20px 13px 44px', cursor: 'pointer' }}
                          onClick={() => toggleSubsecao(sub.id_subsecao)}
                        >
                          <span className="ms" style={{ fontSize: 18, color: 'var(--am-cinza-texto-2)' }}>
                            {subAberta ? 'expand_more' : 'chevron_right'}
                          </span>
                          <div className="flex-grow-1">
                            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--am-tinta)' }}>
                              {sub.numero} — {sub.nome}
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--am-cinza-texto-2)' }}>
                              {requisitosFiltrados.length} requisito(s)
                            </div>
                          </div>
                        </button>

                        {subAberta && (
                          <div className="am-table-wrap" style={{ padding: '0 20px 16px 44px' }}>
                            <table className="am-table">
                              <thead>
                                <tr>
                                  <th>Código</th>
                                  <th>Requisito</th>
                                  <th>Status</th>
                                  <th>Ação corretiva</th>
                                  <th>Vigência</th>
                                  <th>Registrado por</th>
                                  <th />
                                </tr>
                              </thead>
                              <tbody>
                                {requisitosFiltrados.map((r) => {
                                  const avaliacao = avaliacaoPorRequisito.get(r.id_requisito);
                                  return (
                                    <tr
                                      key={r.id_requisito}
                                      id={`requisito-${r.id_requisito}`}
                                      style={
                                        destacado === r.id_requisito
                                          ? { background: 'var(--am-amarelo-nevoa)' }
                                          : undefined
                                      }
                                    >
                                      <td style={{ whiteSpace: 'nowrap' }}>
                                        <span className="d-inline-flex align-items-center gap-1" style={{ fontWeight: 600 }}>
                                          {r.codigo}
                                          {r.core && <CoreBadge />}
                                        </span>
                                      </td>
                                      <td style={{ maxWidth: 360 }} title={r.texto}>
                                        {r.texto}
                                      </td>
                                      <td>
                                        {avaliacao ? (
                                          <StatusBadge status={avaliacao.status} />
                                        ) : (
                                          <Pill tone="outline">Não avaliado</Pill>
                                        )}
                                      </td>
                                      <td>
                                        {avaliacao?.status_acao ? (
                                          <div>
                                            <StatusBadge status={avaliacao.status_acao} />
                                            {avaliacao.prazo_acao && (
                                              <div style={{ fontSize: 11, color: 'var(--am-cinza-texto-2)', marginTop: 2 }}>
                                                até {formatDateOnly(avaliacao.prazo_acao)}
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          '—'
                                        )}
                                      </td>
                                      <td>
                                        {avaliacao ? (
                                          <div>
                                            <StatusBadge status={avaliacao.status_vigencia} />
                                            <div style={{ fontSize: 11, color: 'var(--am-cinza-texto-2)', marginTop: 2 }}>
                                              até {formatDateOnly(avaliacao.data_vencimento_vigencia)}
                                            </div>
                                          </div>
                                        ) : (
                                          '—'
                                        )}
                                      </td>
                                      <td style={{ fontSize: 11, color: 'var(--am-cinza-texto-2)', whiteSpace: 'nowrap' }}>
                                        {avaliacao ? (
                                          avaliacao.criado_por_nome === avaliacao.atualizado_por_nome ? (
                                            <div>{avaliacao.criado_por_nome || '—'}</div>
                                          ) : (
                                            <>
                                              <div>Criado: {avaliacao.criado_por_nome || '—'}</div>
                                              <div>Atualizado: {avaliacao.atualizado_por_nome || '—'}</div>
                                            </>
                                          )
                                        ) : (
                                          '—'
                                        )}
                                      </td>
                                      <td>
                                        <div className="d-flex gap-2">
                                          {avaliacao && (
                                            <button
                                              type="button"
                                              className="am-btn am-btn-outline"
                                              onClick={() => setModalHistorico(r)}
                                              title="Histórico"
                                            >
                                              <span className="ms">history</span>
                                            </button>
                                          )}
                                          {!somenteLeitura && (
                                            <button
                                              type="button"
                                              className="am-btn am-btn-dark"
                                              onClick={() => setModalAvaliar(r)}
                                            >
                                              Avaliar
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {modalAvaliar && (
        <AvaliacaoForm
          requisito={modalAvaliar}
          avaliacaoAtual={avaliacaoPorRequisito.get(modalAvaliar.id_requisito)}
          onClose={() => setModalAvaliar(null)}
          onSalvar={async (dados) => {
            await avaliacoesApi.registrar(idHospital, dados);
            await carregar();
          }}
        />
      )}

      {modalHistorico && (
        <HistoricoModal idHospital={idHospital} requisito={modalHistorico} onClose={() => setModalHistorico(null)} />
      )}
    </div>
  );
}
